import { Tool } from "./tool";
import { FeatureStyle, STREAMTYPE } from "../internal";
import { GEOMETRYTYPE } from "../featuretree/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Vector3 } from "three";
/**
 * @class SurveyWheelMeasureTool
 * @constructor
 */
export class SurveyWheelMeasureTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        this.mouseupeventnr = null;
        this.mousemoveeventnr = null;
        this.mouseouteventnr = null;
        this.resetPointinputevent = null;
        this.resetinputevent = null;
        this.geoframechangeevent = null;
        this.axisprecisefeaturecollection = null;
        this.axisprecisemultipointfeature = null;
        this.axispreciselinestringfeature = null;
        this.axispredictfeaturecollection = null;
        this.axispredictstartlinestringfeature = null;
        this.axispredictendlinestringfeature = null;
        this.axistotalfeaturecollection = null;
        this.axistotallinestringfeature = null;
        this.lrfeaturecollection = null;
        this.lrpointfeature = null;
        this.lrlinestring = null;
        this.startpointfeaturecollection = null;
        this.startauxiliarylinefeature = null;
        this.startpointfeature = null;
        this.endpointfeaturecollection = null;
        this.endpointfeature = null;
        this.endauxiliarylinefeature = null;
        this.highlightfeaturecollection = null;
        this.highlightprecisefeature = null;
        this.highlightpredictstartfeature = null;
        this.highlightpredictendfeature = null;
        this.highlightpointfeature = null;
        this.toolstate = null; // 1 = enabled, 2 = startpointclicked, 3 = endpointclicked, 4 = finished, -1 = disabled
        this.evt = null;
        this.pos = null;
        this.geoframelist = [];
        this.prev_geoframe = null;
        this.extrapollength = 50; // in m
        this.minjumplength = 30; // in m, everything less than this will not be recognised as a jump
        this.axis = null;
        this.startpoint = null;
        this.endpoint = null;
        this.posuvwcoords = null;
        this.startuvwcoords = null;
        this.enduvwcoords = null;
        this.continousdraw_local = true;
        this.len = null;
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const defaultoptions = {
            axislayerstyle: this.toolhandler.defaultstyles.draw,
            axispredictlayerstyle: this.toolhandler.defaultstyles.profiledots,
            auxiliarylinestyle: this.toolhandler.defaultstyles.standard,
            startpointstyle: this.toolhandler.defaultstyles.orthobase,
            endpointstyle: this.toolhandler.defaultstyles.orthobase,
            highlightstyle: new FeatureStyle({
                stylename: "highlight",
                rules: [
                    {
                        filter: "name==precise",
                        symbolizer: "line",
                        params: {
                            "stroke-width": 5,
                            stroke: [0, 1, 0, 1],
                        },
                    },
                    {
                        filter: "name==predict",
                        symbolizer: "line",
                        params: {
                            "stroke-width": 5,
                            stroke: [0, 1, 0, 1],
                            "stroke-dash-width": 3,
                            "stroke-gap-width": 2,
                        },
                    },
                    {
                        filter: "",
                        symbolizer: "point",
                        params: {
                            "font-marker": {
                                name: "circle",
                            },
                            size: 10,
                            stroke: [0, 1, 0, 1],
                        },
                    },
                ],
            }),
            pointsetcbf: null,
            resetPointcbf: null,
            pointsetinput: USERINPUTTYPE.MOUSEUP,
            resetPointinput: USERINPUTTYPE.RIGHTCLICK,
            resetinput: USERINPUTTYPE.KEY_ESC,
            activecursor: "crosshair",
            modifycursor: true,
            continousdraw: true,
            maxdist: 20,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        this.type = "SurveyWheelMeasureTool";
    }
    /**
     * internal function - to activate tool call mmSelectFeatureTool.enable
     * @method _enableTool
     * @public
     */
    _enableTool() {
        this.enabled = true;
        this._subscribeEvents();
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        }
        this.fire(SurveyWheelMeasureTool.enabled, this.options["cbobject"]);
        // Change position of first geoframe, because infra3d can't draw a line at the spawn point.
        const geoframe = this.scene.getCurrentGeoframe();
        geoframe.easting += 0.0000001;
        geoframe.northing += 0.0000001;
        this.geoframelist = [geoframe];
        this.toolstate = 1;
        //create the drawing layers
        this._initializeFeatures();
    }
    /**
     * Initializes the Features
     * @private
     */
    _initializeFeatures() {
        this.axisprecisefeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.axislayerstyle, null, "mmSurveyWheelMeasureTool_internal", false);
        this.axisprecisemultipointfeature = this.axisprecisefeaturecollection.createFeature();
        this.axisprecisemultipointfeature.geometry = this.axisprecisemultipointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.axispreciselinestringfeature = this.axisprecisefeaturecollection.createFeature();
        this.axispreciselinestringfeature.geometry = this.axispreciselinestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.axispredictfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.axispredictlayerstyle, null, "mmAxisPrediction_internal", false);
        this.axispredictstartlinestringfeature = this.axispredictfeaturecollection.createFeature();
        this.axispredictstartlinestringfeature.geometry = this.axispredictstartlinestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.axispredictendlinestringfeature = this.axispredictfeaturecollection.createFeature();
        this.axispredictendlinestringfeature.geometry = this.axispredictendlinestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.axistotalfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.axislayerstyle, null, "mmAxisTotal_internal", false);
        this.axistotallinestringfeature = this.axistotalfeaturecollection.createFeature();
        this.axistotallinestringfeature.geometry = this.axistotallinestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.lrfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.auxiliarylinestyle, null, "mmLinRefAuxiliaryLine_internal");
        this.lrlinestring = this.lrfeaturecollection.createFeature();
        this.lrlinestring.geometry = this.lrlinestring.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.lrpointfeature = this.lrfeaturecollection.createFeature();
        this.lrpointfeature.geometry = this.lrpointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.startpointfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.startpointstyle, null, "mmStartPoint_internal");
        this.startpointfeature = this.startpointfeaturecollection.createFeature();
        this.startpointfeature.geometry = this.startpointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.startauxiliarylinefeature = this.startpointfeaturecollection.createFeature();
        this.startauxiliarylinefeature.geometry = this.startauxiliarylinefeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.endpointfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.endpointstyle, null, "mmEndPoint_internal");
        this.endpointfeature = this.endpointfeaturecollection.createFeature();
        this.endpointfeature.geometry = this.endpointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.endauxiliarylinefeature = this.endpointfeaturecollection.createFeature();
        this.endauxiliarylinefeature.geometry = this.endauxiliarylinefeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.highlightfeaturecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.highlightstyle, null, "highlight_internal");
        this.highlightprecisefeature = this.highlightfeaturecollection.createFeature();
        this.highlightprecisefeature.properties.name = "precise";
        this.highlightprecisefeature.geometry = this.highlightprecisefeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.highlightpredictstartfeature = this.highlightfeaturecollection.createFeature();
        this.highlightpredictstartfeature.properties.name = "predict";
        this.highlightpredictstartfeature.geometry = this.highlightpredictstartfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.highlightpredictendfeature = this.highlightfeaturecollection.createFeature();
        this.highlightpredictendfeature.properties.name = "predict";
        this.highlightpredictendfeature.geometry = this.highlightpredictendfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.highlightpointfeature = this.highlightfeaturecollection.createFeature();
        this.highlightpointfeature.geometry = this.highlightpointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
    }
    /**
     * internal function - to disable tool call mmSelectFeatureTool.disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        this.enabled = false;
        this.reset(null);
        this._unSubscribeEvents();
        this._destroyFeaturecollections();
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle("default");
            this.scene.getMeasureModeHandler().hideMeasureModeCursor();
        }
        this.fire(SurveyWheelMeasureTool.disabled, this.options["cbobject"]);
        this.toolstate = -1;
        this.startpoint = null;
        this.startuvwcoords = null;
        this.endpoint = null;
        this.enduvwcoords = null;
        this.axis = null;
        this.geoframelist = [];
        this.prev_geoframe = null;
    }
    /**
     * Destroys all Feature Collections
     * @private
     */
    _destroyFeaturecollections() {
        this.axisprecisefeaturecollection.destroy();
        this.axispredictfeaturecollection.destroy();
        this.axistotalfeaturecollection.destroy();
        this.startpointfeaturecollection.destroy();
        this.endpointfeaturecollection.destroy();
        this.lrfeaturecollection.destroy();
        this.highlightfeaturecollection.destroy();
    }
    /**
     * subscribe all needed events
     * @method _subscribeEvents
     * @private
     */
    _subscribeEvents() {
        //check if events aren't already subscribed
        if (!this.enabled || this.mouseupeventnr != null)
            return;
        this.mouseupeventnr = this.scene.userInputHandler.subscribeEvent(this.options.pointsetinput, this._onMouseUp, this);
        this.resetinputevent = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this._reset, this);
        this.resetPointinputevent = this.scene.userInputHandler.subscribeEvent(this.options.resetPointinput, this._resetPoint, this);
        if (this.options.continousdraw) {
            this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        }
        this.mouseouteventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEOUT, this._onMouseOut, this);
        this.geoframechangeevent = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this._onGeoFrameChanged, this);
        if (this.options.enablecbf != null) {
            this.on(SurveyWheelMeasureTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(SurveyWheelMeasureTool.disabled, this.options.disablecbf);
        }
        if (this.options.pointsetcbf != null) {
            this.on(SurveyWheelMeasureTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.on(SurveyWheelMeasureTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.errorcbf != null) {
            this.on(SurveyWheelMeasureTool.error, this.options.errorcbf);
        }
        if (this.options.resetcbf != null) {
            this.on(SurveyWheelMeasureTool.reset, this.options.resetcbf);
        }
        if (this.options.resetPointcbf != null) {
            this.on(SurveyWheelMeasureTool.resetpoint, this.options.resetPointcbf);
        }
    }
    /**
     * Unsubcribe all Events
     * @method _unSubscribeEvents
     * @private
     */
    _unSubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(this.options.pointsetinput, this.mouseupeventnr);
        this.mouseupeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEOUT, this.mouseouteventnr);
        this.mouseouteventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.resetinput, this.resetinputevent);
        this.resetinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.resetPointinput, this.resetPointinputevent);
        this.resetPointinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this.geoframechangeevent);
        this.geoframechangeevent = null;
        this.geoframelist = [];
        if (this.options.enablecbf != null) {
            this.off(SurveyWheelMeasureTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(SurveyWheelMeasureTool.disabled, this.options.disablecbf);
        }
        if (this.options.pointsetcbf != null) {
            this.off(SurveyWheelMeasureTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.off(SurveyWheelMeasureTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.errorcbf != null) {
            this.off(SurveyWheelMeasureTool.error, this.options.errorcbf);
        }
        if (this.options.resetcbf != null) {
            this.off(SurveyWheelMeasureTool.reset, this.options.resetcbf);
        }
        if (this.options.resetPointcbf != null) {
            this.off(SurveyWheelMeasureTool.resetpoint, this.options.resetPointcbf);
        }
    }
    /**
     * internal function mouseup
     * @method _onMouseUp
     * @param evt
     * @private
     */
    _onMouseUp(evt) {
        if (evt.isDragging) {
            if (this.options.continousdraw && this.continousdraw_local) {
                this._measureOnMouseMove(evt);
            }
        }
        else if (evt.button === 0) {
            this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), evt.offsetX, evt.offsetY, this.options.maxdist, (pos) => {
                this._pointFoundMouseUp(pos, evt);
            }, (msg) => {
                this._pointNotFound(msg);
            }, this.scene.getMeasureModeHandler().mouseMoveMeasureMode);
        }
    }
    /**
     * If there was found a point, when the mouse goes up.
     * @param pos { MeasurePoint }
     * @param evt
     * @private
     */
    _pointFoundMouseUp(pos, evt) {
        switch (this.toolstate) {
            case 1: // 1 = enabled
                this._setStartPoint(pos);
                if (this.axis) {
                    this._continousDraw(pos);
                    this.fire(SurveyWheelMeasureTool.pointset, this.options["cbobject"], evt, this.len, this.toolstate, this.axis);
                }
                break;
            case 2: // 2 = startpointclicked
                if (this.axis) {
                    this._setEndPoint(pos);
                    this.fire(SurveyWheelMeasureTool.pointset, this.options["cbobject"], evt, this.len, this.toolstate, this.axis);
                }
                break;
            case 3: // 3 = endpointclicked
                this._setEndPoint(pos);
                this.fire(SurveyWheelMeasureTool.pointset, this.options["cbobject"], evt, this.len, this.toolstate, this.axis);
                break;
        }
    }
    /**
     * internal function mousemove
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.lrpointfeature.ishidden) {
            this.lrpointfeature.show();
        }
        if (this.options.continousdraw && this.continousdraw_local) {
            this._measureOnMouseMove(evt);
        }
    }
    /**
     * internal function mouseout
     * @method _onMouseOut
     * @param evt
     * @private
     */
    _onMouseOut(evt) {
        this.lrpointfeature.hide();
        this.lrpointfeature.update(true);
    }
    /**
     * Calculates the world coordinates of the cursor position and displays it as a point in scene
     * @method _measureOnMouseMove
     * @param evt
     * @private
     */
    _measureOnMouseMove(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        this.evt = evt;
        this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist, (pos) => {
            this._continousDraw(pos);
        }, (msg) => {
            this._pointNotFound(msg);
        }, this.scene.getMeasureModeHandler().mouseMoveMeasureMode);
    }
    /**
     * succescbf of _measureOnMouseMove, if a point was found at the cursor position.
     * @method _pointfound
     * @param { MeasurePoint } pos the global coordinates
     * @private
     */
    _continousDraw(pos) {
        this.pos = pos;
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        }
        this._drawCursorPoint(pos);
        this.lrpointfeature.show();
        if ((this.toolstate === 1 && this.axis) || this.toolstate === 2) {
            this._drawAuxiliaryLine();
        }
        this.len = null;
        if (this.toolstate == 2 && this.axis) {
            this.len = Number(Math.abs(this.posuvwcoords.lrcoords[0] - this.startuvwcoords.lrcoords[0]).toFixed(2));
        }
        else {
            this.len = null;
        }
        this.fire(SurveyWheelMeasureTool.continousdraw, this.options["cbobject"], this.len, this.toolstate, this.axis);
    }
    /**
     * failedcbf of _measureOnMouseMove, if no point was found at the coursor position.
     * @method _pointNotFound
     * @param { string } msg The fail Message
     * @private
     */
    _pointNotFound(msg) {
        this.fire(SurveyWheelMeasureTool.error, this.options["cbobject"], msg);
        if (this.options.modifycursor && this.options.continousdraw === true) {
            this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
        }
        if (this.toolstate === 1 && !this.axis) {
            this.lrpointfeature.hide();
        }
    }
    /**
     * function to draw all the features.
     * @method _drawCursorPoint
     * @param { MeasurePoint } coords the global coordinates
     * @private
     */
    _drawCursorPoint(coords) {
        // Draw the point at the Cursor Location
        this.lrpointfeature.geometry.coordinates = [[coords.Easting, coords.Northing, coords.Height]];
        this.lrfeaturecollection.update();
    }
    /**
     * Draws the Auxiliary Line
     * @method _drawAuxiliaryLine
     * @private
     */
    _drawAuxiliaryLine() {
        if (this.axis) {
            this.posuvwcoords = this.axis.xyz2uvw(this.pos.Easting, this.pos.Northing, this.pos.Height);
            if (this.posuvwcoords) {
                this.lrlinestring.geometry.coordinates = [
                    [this.pos.Easting, this.pos.Northing, this.pos.Height],
                    [this.posuvwcoords.axpoint[0], this.posuvwcoords.axpoint[1], this.pos.Height],
                    [this.posuvwcoords.axpoint[0], this.posuvwcoords.axpoint[1], this.posuvwcoords.axpoint[2]],
                ];
                this.lrfeaturecollection.update();
            }
        }
    }
    /**
     * function to actualize this.geoframelist
     * @method _onGeoFrameChanged
     * @param { GeoFrame } geoframe
     * @private
     */
    _onGeoFrameChanged(geoframe) {
        // Change position of geoframes, because infra3d can't draw a line at the spawn point.
        geoframe.easting += 0.0000001;
        geoframe.northing += 0.0000001;
        this._appendToGeoframelist(geoframe);
        this.prev_geoframe = geoframe;
        this._updateAxis();
    }
    /**
     * Appends the passed geoframe to the geoframelist. Checks, where the geoframe needs to be inserted
     * to the geoframelist.
     * @param geoframe
     * @private
     */
    _appendToGeoframelist(geoframe) {
        if (this.prev_geoframe == null) {
            this.geoframelist.push(geoframe);
        }
        else if (this.geoframelist.findIndex((x) => x.timestamp === geoframe.timestamp) === -1) {
            const geoframeindex = this.geoframelist.findIndex((x) => x.timestamp == this.prev_geoframe.timestamp);
            if (geoframeindex == 0) {
                // Goes backwards -> Insert GeoFrame at the beginning
                const dist = Math.sqrt(Math.pow(geoframe.easting - this.geoframelist[0].easting, 2) +
                    Math.pow(geoframe.northing - this.geoframelist[0].northing, 2));
                if (dist > this.minjumplength) {
                    this.fire(SurveyWheelMeasureTool.reset, this.options["cbobject"]);
                }
                else {
                    if (this.geoframelist.length >= 2) {
                        if (!((this.geoframelist[1].timestamp < geoframe.timestamp &&
                            this.geoframelist[0].timestamp > geoframe.timestamp) ||
                            (this.geoframelist[1].timestamp > geoframe.timestamp &&
                                this.geoframelist[0].timestamp < geoframe.timestamp)) ||
                            this.geoframelist[0].timestamp.toString().slice(0, 13) ===
                                geoframe.timestamp.toString().slice(0, 13)) {
                            this.geoframelist.splice(geoframeindex, 0, geoframe);
                        }
                    }
                    else {
                        this.geoframelist.splice(geoframeindex, 0, geoframe);
                    }
                }
            }
            else if (geoframeindex == this.geoframelist.length - 1) {
                // Goes forward -> Insert GeoFrame at the End
                const dist = Math.sqrt(Math.pow(geoframe.easting - this.geoframelist[this.geoframelist.length - 1].easting, 2) +
                    Math.pow(geoframe.northing - this.geoframelist[this.geoframelist.length - 1].northing, 2));
                if (dist > this.minjumplength) {
                    this.fire(SurveyWheelMeasureTool.reset, this.options["cbobject"]);
                }
                else {
                    if (this.geoframelist.length >= 2) {
                        if (!((this.geoframelist[this.geoframelist.length - 1].timestamp < geoframe.timestamp &&
                            this.geoframelist[this.geoframelist.length - 2].timestamp > geoframe.timestamp) ||
                            (this.geoframelist[this.geoframelist.length - 1].timestamp > geoframe.timestamp &&
                                this.geoframelist[this.geoframelist.length - 2].timestamp < geoframe.timestamp)) ||
                            this.geoframelist[this.geoframelist.length - 1].timestamp.toString().slice(0, 13) !==
                                geoframe.timestamp.toString().slice(0, 13)) {
                            this.geoframelist.splice(geoframeindex + 1, 0, geoframe);
                        }
                    }
                    else {
                        this.geoframelist.splice(geoframeindex + 1, 0, geoframe);
                    }
                }
            }
            else if (geoframeindex != -1) {
                // Switch -> delete End and insert at the End
                const dist = Math.sqrt(Math.pow(geoframe.easting - this.geoframelist[geoframeindex + 1].easting, 2) +
                    Math.pow(geoframe.northing - this.geoframelist[geoframeindex + 1].northing, 2));
                if (dist > this.minjumplength) {
                    this.fire(SurveyWheelMeasureTool.reset, this.options["cbobject"]);
                }
                else {
                    if (!((this.geoframelist[geoframeindex - 1].timestamp < geoframe.timestamp &&
                        this.geoframelist[geoframeindex + 1].timestamp > geoframe.timestamp) ||
                        (this.geoframelist[geoframeindex - 1].timestamp > geoframe.timestamp &&
                            this.geoframelist[geoframeindex + 1].timestamp < geoframe.timestamp)) ||
                        this.geoframelist[geoframeindex - 1].timestamp.toString().slice(0, 13) !==
                            geoframe.timestamp.toString().slice(0, 13)) {
                        this.geoframelist = this.geoframelist.slice(0, geoframeindex + 1);
                        this.geoframelist.push(geoframe);
                    }
                }
            }
        }
    }
    /**
     * Updates the Axis
     * @method _updateAxis
     * @private
     */
    _updateAxis() {
        this._calculateAxisLine();
        this.axis = this.scene
            .getLRHandler()
            .createAxis(JSON.parse(this.axistotalfeaturecollection.toGeoJsonString()), "length", "offset", ["id"], "name");
        // Update u-Values of Start- and Endpoint
        if (this.startpoint != null) {
            // Calculate Startpoint
            this._setStartPoint(this.startpoint);
            this.fire(SurveyWheelMeasureTool.continousdraw, this.options["cbobject"], this.len, this.toolstate, this.axis);
        }
        if (this.endpoint != null) {
            // Calculate Endpoint
            this._setEndPoint(this.endpoint);
            this.fire(SurveyWheelMeasureTool.continousdraw, this.options["cbobject"], this.len, this.toolstate, this.axis);
        }
    }
    /**
     * Draws the Axis-Line from the Frames in geoframelist.
     * @method _drawAxisLine
     * @private
     */
    _calculateAxisLine() {
        const coords = [];
        this.geoframelist.forEach((geoframe) => {
            if (geoframe.getMeasureInfo().streamtype === STREAMTYPE.STEREO) {
                const centercoords = SurveyWheelMeasureTool._calculateCenterCoords(geoframe);
                coords.push([centercoords[0], centercoords[1], centercoords[2] - geoframe.getMeasureInfo().height]);
            }
            else {
                coords.push([geoframe.easting, geoframe.northing, geoframe.height - geoframe.getMeasureInfo().height]);
            }
        });
        // Extrapolation beginning
        const dx_start = coords[0][0] - coords[1][0];
        const dy_start = coords[0][1] - coords[1][1];
        const dh_start = coords[0][2] - coords[1][2];
        const len_start = (dx_start ** 2 + dy_start ** 2 + dh_start ** 2) ** 0.5;
        const scale_start = this.extrapollength / len_start;
        const dx_scale_start = dx_start * scale_start + coords[0][0];
        const dy_scale_start = dy_start * scale_start + coords[0][1];
        const dh_scale_start = dh_start * scale_start + coords[0][2];
        coords.splice(0, 0, [dx_scale_start, dy_scale_start, dh_scale_start]);
        // Extrapolation end
        const dx_end = coords[coords.length - 1][0] - coords[coords.length - 2][0];
        const dy_end = coords[coords.length - 1][1] - coords[coords.length - 2][1];
        const dh_end = coords[coords.length - 1][2] - coords[coords.length - 2][2];
        const len_end = (dx_end ** 2 + dy_end ** 2 + dh_end ** 2) ** 0.5;
        const scale_end = this.extrapollength / len_end;
        const x_scale_end = dx_end * scale_end + coords[coords.length - 1][0];
        const y_scale_end = dy_end * scale_end + coords[coords.length - 1][1];
        const h_scale_end = dh_end * scale_end + coords[coords.length - 1][2];
        coords.push([x_scale_end, y_scale_end, h_scale_end]);
        this._drawAxisLine(coords);
    }
    /**
     * Draws the axis line incl. the start- and endpoint extrapolation.
     * @param coords
     * @private
     */
    _drawAxisLine(coords) {
        this.axispreciselinestringfeature.geometry.coordinates = coords.slice(1, coords.length - 1);
        this.axisprecisemultipointfeature.geometry.coordinates = coords.slice(1, coords.length - 1);
        this.axispredictstartlinestringfeature.geometry.coordinates = coords.slice(0, 2);
        this.axispredictendlinestringfeature.geometry.coordinates = coords.slice(coords.length - 2);
        this.axistotallinestringfeature.geometry.coordinates = coords;
        this.axisprecisefeaturecollection.update();
        this.axispredictfeaturecollection.update();
    }
    /**
     * Calculates the Center Coordinates of Stereo-Pictures. With this, the line is centered (like the pano).
     * @param geoframe
     * @private
     */
    static _calculateCenterCoords(geoframe) {
        const R = geoframe.getRotationMatrixAsMat4();
        const vr = new Vector3(geoframe.getMeasureInfo().depthmapmeta.basis / 2, 0, 0);
        const v = vr.applyMatrix4(R);
        const X0 = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        return new Vector3(v.x, v.y, v.z).add(X0).toArray();
    }
    /**
     * Calculates the uvw coords from the given startpoint pos and draws the corresponding line.
     * @method _setStartPoint
     * @param pos { MeasurePoint }
     * @private
     */
    _setStartPoint(pos) {
        this.startpoint = pos;
        this.startpointfeature.geometry.coordinates = [
            [this.startpoint.Easting, this.startpoint.Northing, this.startpoint.Height],
        ];
        this.toolstate = 2;
        if (this.axis) {
            this.startuvwcoords = this.axis.xyz2uvw(this.startpoint.Easting, this.startpoint.Northing, this.startpoint.Height);
            this.startauxiliarylinefeature.geometry.coordinates = [
                [this.startpoint.Easting, this.startpoint.Northing, this.startpoint.Height],
                [this.startuvwcoords.axpoint[0], this.startuvwcoords.axpoint[1], this.startpoint.Height],
                [this.startuvwcoords.axpoint[0], this.startuvwcoords.axpoint[1], this.startuvwcoords.axpoint[2]],
            ];
        }
        this.startpointfeaturecollection.update();
    }
    /**
     * Calculates the uvw coords from the given endpoint pos and draws the corresponding line.
     * @method _setEndPoint
     * @param pos { MeasurePoint }
     * @private
     */
    _setEndPoint(pos) {
        this.endpoint = pos;
        this.enduvwcoords = this.axis.xyz2uvw(this.endpoint.Easting, this.endpoint.Northing, this.endpoint.Height);
        const coords = [
            [this.endpoint.Easting, this.endpoint.Northing, this.endpoint.Height],
            [this.enduvwcoords.axpoint[0], this.enduvwcoords.axpoint[1], this.endpoint.Height],
            [this.enduvwcoords.axpoint[0], this.enduvwcoords.axpoint[1], this.enduvwcoords.axpoint[2]],
        ];
        this.toolstate = 3;
        this.endpointfeature.geometry.coordinates = [
            [this.endpoint.Easting, this.endpoint.Northing, this.endpoint.Height],
        ];
        this.endauxiliarylinefeature.geometry.coordinates = coords;
        this.endpointfeaturecollection.update();
        this.lrfeaturecollection.hide();
        if (this.axis) {
            this.len = Number(Math.abs(this.enduvwcoords.lrcoords[0] - this.startuvwcoords.lrcoords[0]).toFixed(2));
        }
        else {
            this.len = null;
        }
        this._drawHighlightLine(pos);
    }
    /**
     * Is called on resetPointinput and calls the different resetfunctions (depending on toolstate).
     * @method _finnish
     * @private
     */
    _resetPoint() {
        switch (this.toolstate) {
            case 1:
                this.fire(SurveyWheelMeasureTool.disabled, this.options["cbobject"]);
                this.fire(SurveyWheelMeasureTool.enabled, this.options["cbobject"]);
                break;
            case 2:
                this._resetStartPoint();
                break;
            case 3:
                this.continousdraw_local = true;
                this.lrfeaturecollection.show();
                this._resetEndPoint();
                break;
        }
    }
    /**
     * Resets the startpoint.
     * @method _resetStartPoint
     * @private
     */
    _resetStartPoint() {
        this.toolstate = 1;
        this.startpoint = null;
        this.startuvwcoords = null;
        this.startpointfeature.geometry.clear();
        this.startauxiliarylinefeature.geometry.clear();
        this.startpointfeaturecollection.update();
        this.fire(SurveyWheelMeasureTool.resetpoint, this.options["cbobject"]);
    }
    /**
     * Resets the endpoint.
     * @method _resetEndPoint
     * @private
     */
    _resetEndPoint() {
        this.toolstate = 2;
        this.endpoint = null;
        this.enduvwcoords = null;
        this.endpointfeature.geometry.clear();
        this.endauxiliarylinefeature.geometry.clear();
        this.endpointfeaturecollection.update();
        this.highlightprecisefeature.geometry.clear();
        this.highlightpredictstartfeature.geometry.clear();
        this.highlightpredictendfeature.geometry.clear();
        this.highlightpointfeature.geometry.clear();
        this.highlightfeaturecollection.update();
    }
    /**
     * Resets the Tool.
     * @private
     */
    _reset() {
        this.fire(SurveyWheelMeasureTool.reset, this.options["cbobject"]);
    }
    /**
     * Draws the highlighting line
     * @private
     */
    _drawHighlightLine(pos) {
        let startlrcoords = this.axis.xyz2uvw(this.startpoint.Easting, this.startpoint.Northing, this.startpoint.Height);
        let endlrcoords = this.axis.xyz2uvw(pos.Easting, pos.Northing, pos.Height);
        // Change direction, that startlrcoords.vertexidx is always smaller than endlrcoords.vertexidx
        if (startlrcoords.vertexidx > endlrcoords.vertexidx) {
            const start = startlrcoords;
            startlrcoords = endlrcoords;
            endlrcoords = start;
        }
        const coordsprecise = [];
        const coordsstartpredict = [];
        const pointcoords = [];
        if (startlrcoords.vertexidx === 0 && endlrcoords.vertexidx === 0) {
            coordsstartpredict.push(startlrcoords.axpoint);
            coordsstartpredict.push(endlrcoords.axpoint);
        }
        else if (startlrcoords.vertexidx === 0) {
            coordsstartpredict.push(startlrcoords.axpoint);
            coordsstartpredict.push(this.axis.axis["features"][0].geometry.coordinates[1]);
        }
        else {
            coordsprecise.push(startlrcoords.axpoint);
        }
        for (let i = startlrcoords.vertexidx + 1; i <= endlrcoords.vertexidx; i++) {
            coordsprecise.push(this.axis.axis["features"][0].geometry.coordinates[i]);
            pointcoords.push(this.axis.axis["features"][0].geometry.coordinates[i]);
        }
        const coordsendpredict = [];
        if (endlrcoords.vertexidx === this.axis.axis["features"][0].geometry.coordinates.length - 2 &&
            startlrcoords.vertexidx === this.axis.axis["features"][0].geometry.coordinates.length - 2 &&
            endlrcoords.vertexidx !== 0) {
            coordsendpredict.push(startlrcoords.axpoint);
            coordsendpredict.push(endlrcoords.axpoint);
        }
        else if (endlrcoords.vertexidx === this.axis.axis["features"][0].geometry.coordinates.length - 2 &&
            endlrcoords.vertexidx !== 0) {
            coordsendpredict.push(endlrcoords.axpoint);
            coordsendpredict.push(this.axis.axis["features"][0].geometry.coordinates[this.axis.axis["features"][0].geometry.coordinates.length - 2]);
        }
        else {
            coordsprecise.push(endlrcoords.axpoint);
        }
        this.highlightprecisefeature.geometry.coordinates = coordsprecise;
        this.highlightpredictstartfeature.geometry.coordinates = coordsstartpredict;
        this.highlightpredictendfeature.geometry.coordinates = coordsendpredict;
        this.highlightpointfeature.geometry.coordinates = pointcoords;
        this.highlightfeaturecollection.update();
    }
}
SurveyWheelMeasureTool.enabled = "enabled";
SurveyWheelMeasureTool.disabled = "disabled";
SurveyWheelMeasureTool.pointset = "pointset";
SurveyWheelMeasureTool.continousdraw = "continousdraw";
SurveyWheelMeasureTool.error = "error";
SurveyWheelMeasureTool.reset = "reset";
SurveyWheelMeasureTool.resetpoint = "resetpoint";
//# sourceMappingURL=surveywheelmeasuretool.js.map